import { Formik, FormikValues } from 'formik';
import { useHistory } from 'react-router-dom';
import pickBy from 'lodash.pickby';
import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { Button } from '../../components/styled/button';
import { usePollForUpdates } from '../../hooks/usePollForUpdates';
import { initiateReferralUrl, listReferralsUrl } from '../../lib/apiUrls';
import { ReferralSchema, initialValues, FormFields } from './validation';
import { ReferralForm } from './form';
import stubAgencyName from '../../lib/stubAgencyName';
import { PageHeader } from '../../components/PageHeader';
import type { ReferralData } from '../../types/referral';

type UnsanitizedData = FormFields & { assignedAgency: string };
type SanitizedData = Partial<UnsanitizedData>;

const CreateReferral: React.FC = () => {
  const {
    isUpdateDetected,
    pollingController,
    setApiResult,
    pollForUpdates,
    updateHandled,
  } = usePollForUpdates<ReferralData>();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const history = useHistory();

  const { data: referralListData } = useSWR<ReferralData>(
    listReferralsUrl(stubAgencyName),
    {
      refreshInterval: pollingController,
    }
  );

  useEffect(() => {
    setApiResult(referralListData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralListData]);

  useEffect(() => {
    if (isUpdateDetected) {
      updateHandled();
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateDetected]);

  const onSubmit = async (values: FormFields) => {
    setHasSubmitted(true);

    const unsanitizedData: UnsanitizedData = {
      ...values,
      assignedAgency: stubAgencyName,
    };

    const sanitizedData: SanitizedData = pickBy(
      unsanitizedData,
      value => value !== ''
    );

    try {
      await fetch(initiateReferralUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: sanitizedData }),
      });
      pollForUpdates();
    } catch {
      // eslint-disable-next-line no-console
      console.log(
        '*** TRIED TO SUBMIT:',
        JSON.stringify({ data: sanitizedData })
      );
      setHasSubmitted(false);
    }
  };
  const formRef = useRef<FormikValues | null>();
  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  return (
    <>
      <PageHeader title={'Create Referral'}>
        <Button variant={'text'} onClick={history.goBack}>
          Cancel
        </Button>
        <Button disabled={hasSubmitted} onClick={handleSubmit}>
          Save draft
        </Button>
      </PageHeader>

      <Formik
        validationSchema={ReferralSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={theRef => (formRef.current = theRef)}
      >
        {formik => {
          return <ReferralForm formik={formik} />;
        }}
      </Formik>
    </>
  );
};

export { CreateReferral };
